import React, { useRef, useState } from "react";
import { motion } from "framer-motion";

export const FABRICS = {
  black: { name: "Black", fill: "#191919", shade: "#0b0b0b", collar: "#000000", heather: false },
  white: { name: "White", fill: "#f2f2f2", shade: "#d6d6d6", collar: "#cfcfcf", heather: false },
  gray: { name: "Heather Gray", fill: "#b7b9bd", shade: "#9a9ca1", collar: "#8f9195", heather: true },
  navy: { name: "Navy", fill: "#20293f", shade: "#141b2c", collar: "#0f1421", heather: false },
  red: { name: "Red", fill: "#b52a2f", shade: "#861f23", collar: "#6e181c", heather: false },
};

const TEE_PATH =
  "M115,55 L70,80 L20,150 L75,210 L120,178 L120,470 L340,470 L340,178 L385,210 L440,150 L390,80 L345,55 C330,92 288,112 230,112 C172,112 130,92 115,55 Z";

export const TShirtMockup = ({ fabric = "black", graphicUrl, scale = 60, view = "front", resetKey }) => {
  const printRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const f = FABRICS[fabric] || FABRICS.black;
  const isBack = view === "back";
  // back graphics sit slightly higher/wider on the shoulders
  const printBox = isBack
    ? { left: "29%", top: "24%", width: "42%", height: "38%" }
    : { left: "31%", top: "28%", width: "38%", height: "36%" };

  return (
    <div className="relative w-full max-w-[560px] aspect-square select-none" data-testid="tshirt-mockup">
      <span className="absolute top-2 left-2 z-10 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] border border-[#262626] px-2 py-0.5 bg-black/40" data-testid="mockup-view-label">
        {isBack ? "Back" : "Front"}
      </span>
      <svg viewBox="0 0 460 520" className="w-full h-full drop-shadow-2xl">
        <defs>
          <linearGradient id="teeShade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.10" />
            <stop offset="55%" stopColor="#000000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.28" />
          </linearGradient>
          <pattern id="heather" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="4" height="4" fill={f.fill} />
            <rect width="2" height="4" fill={f.shade} opacity="0.35" />
          </pattern>
        </defs>
        <path d={TEE_PATH} fill={f.heather ? "url(#heather)" : f.fill} stroke={f.shade} strokeWidth="2" />
        <path d={TEE_PATH} fill="url(#teeShade)" />
        {/* collar */}
        {isBack ? (
          <>
            {/* back neck ribbing */}
            <path
              d="M180,52 C202,66 258,66 280,52 L276,44 C256,54 204,54 184,44 Z"
              fill={f.collar}
              opacity="0.9"
            />
            <path
              d="M184,60 C205,70 255,70 276,60"
              fill="none"
              stroke={f.shade}
              strokeWidth="2"
              opacity="0.6"
            />
          </>
        ) : (
          <path
            d="M175,58 C195,86 265,86 285,58 C270,72 255,80 230,80 C205,80 190,72 175,58 Z"
            fill={f.collar}
            opacity="0.9"
          />
        )}
        {/* sleeve seam hints */}
        <path d="M120,178 L120,470" stroke={f.shade} strokeWidth="1.5" opacity="0.5" fill="none" />
        <path d="M340,178 L340,470" stroke={f.shade} strokeWidth="1.5" opacity="0.5" fill="none" />
        {/* back center seam hint */}
        {isBack && (
          <path d="M230,66 L230,470" stroke={f.shade} strokeWidth="1" opacity="0.25" fill="none" />
        )}
      </svg>

      {/* Print / drag area */}
      <div
        ref={printRef}
        className="absolute"
        style={printBox}
      >
        <div
          className={`w-full h-full border border-dashed transition-colors ${
            dragging ? "border-[#D4FF00]/80" : "border-white/15"
          } flex items-center justify-center`}
        >
          {graphicUrl && (
            <motion.div
              key={resetKey}
              drag
              dragConstraints={printRef}
              dragMomentum={false}
              dragElastic={0.02}
              onDragStart={() => setDragging(true)}
              onDragEnd={() => setDragging(false)}
              whileTap={{ cursor: "grabbing" }}
              className="cursor-grab active:cursor-grabbing"
              style={{ width: `${scale}%` }}
              data-testid="mockup-graphic"
            >
              <img
                src={graphicUrl}
                alt="graphic"
                draggable={false}
                className="w-full h-auto pointer-events-none"
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};
